import React, { createContext, memo, ReactNode, useMemo } from 'react';
import { authService } from 'domain/auth/api/authService';
import useLocalStorage from 'hooks/useLocalStorage/useLocalStorage';
import { getUserRoleFromToken } from 'utils/jwt';
import { UserRole } from 'types/auth';

type AuthState = {
  accessToken: string;
  refreshToken: string;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  userRole?: UserRole;
};

type Props = {
  children: ReactNode;
};

function AuthProvider({ children }: Props) {
  const [accessToken, setAccessToken] = useLocalStorage('accessToken');
  const [refreshToken, setRefreshToken] = useLocalStorage('refreshToken');

  const handleSignIn = async (email: string, password: string) => {
    const tokenResponse = await authService.signIn(email, password);
    setAccessToken(tokenResponse.accessToken);
    setRefreshToken(tokenResponse.refreshToken);
  };

  async function handleSignOut() {
    setAccessToken(null);
  }

  const userRole = useMemo(() => {
    return accessToken ? getUserRoleFromToken(accessToken as string) : undefined;
  }, [accessToken]);

  const value = {
    accessToken: accessToken as string,
    refreshToken: refreshToken as string,
    signIn: handleSignIn,
    signOut: handleSignOut,
    userRole,
  };
  const { Provider } = AuthContext;

  return <Provider value={value}>{children}</Provider>;
}

const AuthContext = createContext<AuthState>({} as AuthState);

export { AuthContext };
export default memo(AuthProvider);
